import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import {
  fetchPurchaseOrderById,
  deletePurchaseOrder,
  clearError,
  clearSuccess,
} from "../../redux/slices/inventorySlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfoPill from "../Common/InfoPill";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
const PurchaseOrderDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    currentPurchaseOrder,
    isLoading: isLoadingPurchaseOrder,
    error,
    success,
    isDeleting: isDeletingPurchaseOrder,
  } = useSelector((state) => state.inventory);

  // Use refs to track if we've already shown toasts for current messages
  const lastErrorRef = useRef(null);
  const lastSuccessRef = useRef(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchPurchaseOrderById(id));
    }
    // Clear any existing messages when component mounts
    dispatch(clearError());
    dispatch(clearSuccess());
  }, [dispatch, id]);

  // Clear messages and show toast notifications (only once per message)
  useEffect(() => {
    if (error && error !== lastErrorRef.current) {
      // Handle both string and object error messages
      const errorMessage =
        typeof error === "string"
          ? error
          : error.message || "An error occurred";
      toast.error(errorMessage);
      lastErrorRef.current = error;
      // Clear error immediately after showing toast
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success && success !== lastSuccessRef.current) {
      toast.success(success);
      lastSuccessRef.current = success;
      // Clear success immediately after showing toast
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  // Cleanup: Clear messages when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      lastErrorRef.current = null;
      lastSuccessRef.current = null;
    };
  }, [dispatch]);

  const handleBack = () => {
    navigate("/purchase-orders");
  };

  const handleEditPurchaseOrder = () => {
    navigate(`/purchase-orders/edit/${id}`);
  };

  const handleDeletePurchaseOrder = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete purchase order "${currentPurchaseOrder?.invoice_number}"? This action cannot be undone.`
      )
    ) {
      try {
        await dispatch(deletePurchaseOrder(id)).unwrap();
        navigate("/purchase-orders");
      } catch (error) {
        console.error("Failed to delete purchase order:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatDateToDDMMYYYY(dateString);
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0.00";
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      paid: { bg: "success", text: "Paid" },
      unpaid: { bg: "danger", text: "Unpaid" },
      partial: { bg: "warning", text: "Partial" },
    };

    const config = statusConfig[status] || { bg: "secondary", text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  // Purchase items table headers
  const purchaseItemsHeaders = [
    {
      prop: "product_name",
      title: "Product Name",
      isSortable: true,
      isFilterable: true,
    },
    {
      prop: "quantity_purchased",
      title: "Quantity",
      isSortable: true,
      cell: (row) => <Badge bg="info">{row.quantity_purchased}</Badge>,
    },
    {
      prop: "purchase_price_per_unit",
      title: "Price per Unit",
      isSortable: true,
      cell: (row) => formatCurrency(row.purchase_price_per_unit),
    },
    {
      prop: "total_price",
      title: "Total Price",
      isSortable: true,
      cell: (row) => (
        <Badge bg="primary">{formatCurrency(row.total_price)}</Badge>
      ),
    },
  ];

  if (isLoadingPurchaseOrder) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!currentPurchaseOrder) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Purchase Order not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Purchase Orders
          </Button>
        </div>
      </Container>
    );
  }

  const purchaseItems =
    currentPurchaseOrder.items || currentPurchaseOrder.purchase_items || [];

  return (
    <Container className="view-form">
      <Row>
        <Col lg={12}>
          {/* Purchase Order Header */}
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Purchase Order Details
            </h5>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                <i className="bi bi-arrow-left-short  fs-3"></i>
              </Button>
              <Button
                size="sm"
                variant="outline-warning"
                onClick={handleEditPurchaseOrder}
                disabled={isDeletingPurchaseOrder}
              >
                <FaPencilAlt />
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={handleDeletePurchaseOrder}
                disabled={isDeletingPurchaseOrder}
              >
                {/* {isDeletingPurchaseOrder ? "Deleting..." : "Delete"} */}
                <FaRegTrashAlt />
              </Button>
            </div>
          </div>

          {/* Purchase Order Information Card */}
          <Card className="shadow">
            <CardBody>
              {/* Purchase Order Information Section */}
              <Row className="mx-3 my-2 view-form">
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Invoice Number</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentPurchaseOrder?.invoice_number || "-"}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Vendor</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {currentPurchaseOrder?.vendor_business_name ||
                      currentPurchaseOrder?.vendor_name ||
                      "-"}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Purchase Date</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {formatDate(currentPurchaseOrder?.purchase_date)}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Payment Status</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {getPaymentStatusBadge(
                      currentPurchaseOrder?.payment_status
                    )}
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Total Amount</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    <Badge bg="success" style={{ fontSize: "1rem" }}>
                      {formatCurrency(currentPurchaseOrder?.total_amount)}
                    </Badge>
                  </p>
                </Col>
                <Col lg={4} className="my-2">
                  <label className="fw-bold">Created Date</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {formatDate(currentPurchaseOrder?.created_at)}
                  </p>
                </Col>
                {/* <Col lg={4} className="my-2">
                  <label className="fw-bold">Last Updated</label>
                  <p
                    className="mb-0"
                    style={{
                      textAlign: "left",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "5px",
                    }}
                  >
                    {formatDate(currentPurchaseOrder?.updated_at)}
                  </p>
                </Col> */}
              </Row>

              {/* Description Section (if available) */}
              {currentPurchaseOrder?.notes && (
                <Row className="mx-3 my-2 view-form">
                  <Col lg={12} className="my-2">
                    <label className="fw-bold">Notes</label>
                    <p
                      className="mb-0"
                      style={{
                        textAlign: "left",
                        borderBottom: "1px solid #ccc",
                        paddingBottom: "5px",
                        minHeight: "40px",
                      }}
                    >
                      {currentPurchaseOrder?.notes || "No notes provided"}
                    </p>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Purchase Items Section - Matching Category Details Structure */}
      <Row className="mt-4">
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Purchase Items ({purchaseItems.length})
            </h5>
            <div className="d-flex gap-2">
              <Button
                variant="success"
                size="sm"
                onClick={() => navigate(`/purchase-orders/edit/${id}`)}
              >
                <i className="fas fa-plus me-2"></i>
                Add Purchase Item
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              {purchaseItems.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Purchase Items Found</h5>
                  <p className="text-muted">
                    This purchase order doesn't have any items yet.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/purchase-orders/edit/${id}`)}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Add Purchase Items
                  </Button>
                </div>
              ) : (
                <DatatableWrapper
                  body={purchaseItems}
                  headers={purchaseItemsHeaders}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 5,
                      options: [5, 10, 15, 20],
                    },
                  }}
                >
                  {/* Controls */}
                  <div className="d-flex align-items-center justify-content-start flex-wrap gap-2 mb-3">
                    <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
                      <Filter
                        placeholder="Search purchase items..."
                        className="w-100"
                      />
                    </div>
                    <PaginationOptions />
                    <InfoPill left="Total" right={purchaseItems?.length} />
                  </div>

                  {/* Table */}
                  <div className="table-responsive shadow-sm">
                    <table className="table table-striped table-bordered align-middle">
                      <TableHeader />
                      <TableBody />
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-start mt-3">
                    <Pagination classes={{
                      button: "bg-dark",
                    }} />
                  </div>
                </DatatableWrapper>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PurchaseOrderDetails;
